import React, { useEffect } from 'react';
import { ErrorMessage, Field, FieldArray, useFormikContext } from 'formik';
import { Dropdown, TextArea } from 'formik-semantic-ui';
import { Button, Grid, Header, Icon, Image, Message, Popup, Table } from 'semantic-ui-react';
import {
  AttivitaAltoRischioDescriptions,
  DpiDescriptions,
  EEhsMaintenanceType,
  EEhsType,
  ESicurezzaAntincendio,
  EstinguentiDescriptions,
  EUserRoles,
  IAttivitaAltoRischio,
  IBuilding,
  IEhs,
} from '@relab/alstom-ptw-shared';
import styled from 'styled-components';
import { Icons } from '../../../../assets/icons';
import { DatePicker } from '../../../DatePicker';
import { TimePickerComponent } from '../../../TimePickerComponent';
import { FileDownloadLink } from '../../../FileDownloadLink';
import { ExecMemberAdd } from './ExecMemberAdd';
import { MapAreaSelector } from '../../../MapAreaSelector';
import { SignatureModal } from '../SignatureModal';
import { BorderedDiv, SignContainer } from '../StyledEhsComponents';
import { InternalExternalComponent } from './InternalExternalComponent';
import { MisureEControlliComponent } from './MisureEControlliComponent';
import { ResponsabiliIspezioniComponent } from './ResponsabiliIspezioniComponent';
import { InputWithoutEnter } from '../../../InputWithoutEnter';
import { TranslationComponent } from '../../../TranslationComponent';
import { useTranslation } from 'react-i18next';
import { InputLabel } from '../../../styled/InputLabel.styled';

const activities = [
  IAttivitaAltoRischio.loto,
  IAttivitaAltoRischio.sicurezzaElettrica,
  IAttivitaAltoRischio.operazioniSuPartiMeccaniche,
  IAttivitaAltoRischio.lavoriInQuota,
  IAttivitaAltoRischio.lavoriDiScavo,
  IAttivitaAltoRischio.operazioniDiSollevamento,
  IAttivitaAltoRischio.movimentazioneVeicoli,
  IAttivitaAltoRischio.spaziConfinati,
  IAttivitaAltoRischio.sostanzePericolose,
  IAttivitaAltoRischio.lavoriAcquatici,
  IAttivitaAltoRischio.lavoriACaldo,
  IAttivitaAltoRischio.atex,
  IAttivitaAltoRischio.lavoroIsolato,
  IAttivitaAltoRischio.montaggioImpalcature,
  IAttivitaAltoRischio.esposizioneARadiazioni,
  IAttivitaAltoRischio.recuperoFuoriWorkstation,
];

// @ts-ignore
const AttivitaAltoRischio = styled(BorderedDiv)`
  .label-attivita {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1em;

    .help-icon {
      align-self: flex-start;
    }
  }
`;

interface EhsPhaseCreationProps {
  buildings: IBuilding[];
  ehsType: EEhsType;
  userRoles: EUserRoles[];
  pdfExport?: boolean;
}

export const EhsPhaseCreation: React.FC<EhsPhaseCreationProps> = ({ buildings, ehsType, userRoles, pdfExport }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, touched, errors } = useFormikContext<IEhs>();

  useEffect(() => {
    if (
      values.misureEControlli.some((misura) => Object.keys(ESicurezzaAntincendio).includes(misura)) &&
      !values.attivitaAltoRischio.includes(IAttivitaAltoRischio.lavoriACaldo)
    ) {
      const newValue = values.misureEControlli.filter((misura) => !Object.keys(ESicurezzaAntincendio).includes(misura));
      setFieldValue('misureEControlli', newValue);
    }
  }, [setFieldValue, values.attivitaAltoRischio, values.misureEControlli]);

  useEffect(() => {
    setFieldValue('ehsType', ehsType);

    if (ehsType === EEhsType.MAINTENANCE) {
      setFieldValue('maintenanceType', values.maintenanceType ?? EEhsMaintenanceType.STANDARD);
      setFieldValue('internalExternal', values.internalExternal ?? 'external');
    }
  }, [values.maintenanceType, values.internalExternal, ehsType, setFieldValue]);

  useEffect(() => {
    if (
      (!!values.matricolaAnalizzatore || !!values.scadenzaCalibrazione) &&
      !values.attivitaAltoRischio.includes(IAttivitaAltoRischio.spaziConfinati)
    ) {
      setFieldValue('matricolaAnalizzatore', '');
      setFieldValue('scadenzaCalibrazione', '');
    }
  }, [setFieldValue, values.attivitaAltoRischio, values.matricolaAnalizzatore, values.scadenzaCalibrazione]);

  return (
    <>
      {ehsType === EEhsType.MAINTENANCE && (
        <Dropdown
          label={t('Maintenance type')}
          name="maintenanceType"
          options={[
            {
              key: 'STANDARD',
              value: EEhsMaintenanceType.STANDARD,
              text: 'STANDARD',
            },
            {
              key: 'SPAZI_CONFINATI',
              value: EEhsMaintenanceType.SPAZI_CONFINATI,
              text: 'SPAZI CONFINATI',
              disabled:
                !userRoles.includes(EUserRoles.EhsMaintenanceManager) &&
                !userRoles.includes(EUserRoles.EhsMaintenanceExternalCompanyPlus),
            },
          ]}
          inputProps={{
            error: touched.maintenanceType && !!errors.maintenanceType,
            disabled:
              values.maintenanceType === EEhsMaintenanceType.SPAZI_CONFINATI &&
              !userRoles.includes(EUserRoles.EhsMaintenanceManager) &&
              !userRoles.includes(EUserRoles.EhsMaintenanceExternalCompanyPlus),
          }}
        />
      )}

      <TextArea name="description" label={t('Descrizione sintetica del lavoro da eseguire')} />

      <AttivitaAltoRischio>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column textAlign={'left'}>
              <Header>
                <TranslationComponent
                  stringKey={"Segnalare se il lavoro prevede l'esecuzione di una o più delle seguenti attività"}
                />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={6}>
            {activities.map((activity) => (
              <Grid.Column key={activity}>
                <label className={'label-attivita'}>
                  <Field type="checkbox" name="attivitaAltoRischio" value={activity} />
                  <Image src={Icons[activity]} size="tiny" />
                  <Popup
                    trigger={<Icon className={'help-icon'} name="help" />}
                    content={t(AttivitaAltoRischioDescriptions[activity])}
                    position={'left center'}
                  />
                </label>
              </Grid.Column>
            ))}
          </Grid.Row>
        </Grid>
        <ErrorMessage name={'attivitaAltoRischio'}>
          {(errorMessage) => <Message error visible content={errorMessage} />}
        </ErrorMessage>
      </AttivitaAltoRischio>

      <BorderedDiv style={{ padding: 0 }}>
        <div style={{ padding: '1em' }}>
          <Header>
            <TranslationComponent stringKey={'Luogo effettuazione attività'} />
          </Header>
          {!pdfExport && (
            <Header size={'tiny'}>
              <TranslationComponent stringKey={'È obbligatorio selezionare sia dal menù a tendina che sulla mappa'} />
            </Header>
          )}
          <Dropdown
            label={t('Building')}
            name="building"
            options={buildings.map((building) => ({
              key: building._id,
              text: building.name,
              value: building._id,
            }))}
            inputProps={{ error: touched.building && !!errors.building }}
          />
        </div>

        <MapAreaSelector
          selection={buildings.find((building) => building._id === values['building'])?.buildingArea}
          onBuildingSelection={(buildingId) => setFieldValue('building', buildingId)}
          buildings={buildings}
        />
      </BorderedDiv>

      {pdfExport && (
        <>
          <div style={{ breakAfter: 'page' }}></div>
          <div style={{ marginTop: 20 }}>
            <Header textAlign={'center'}>{`EHS #${values.sequentialNumber} - ${values.name}`}</Header>
          </div>
        </>
      )}

      <BorderedDiv>
        <Header>
          <TranslationComponent stringKey={'Durata ed avvio (pianificazione attività)'} />
        </Header>

        <Grid>
          <Grid.Row>
            <Grid.Column columns={2}>
              <DatePicker label={t('Data inizio lavori')} name="startDate" />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column width={3}>
              <TimePickerComponent label={t('Ora inizio lavori')} name="startHour" inputProps={{ showSecond: false }} />
            </Grid.Column>
            <Grid.Column width={3}>
              <TimePickerComponent label={t('Ora fine lavori')} name="endHour" inputProps={{ showSecond: false }} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column width={3}>
              <InputLabel>
                <Field type="radio" name="internalExternal" value="internal" />
                <TranslationComponent stringKey={'Internal'} />
              </InputLabel>
            </Grid.Column>
            <Grid.Column width={3}>
              <InputLabel>
                <Field type="radio" name="internalExternal" value="external" />
                <TranslationComponent stringKey={'External'} />
              </InputLabel>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <ErrorMessage name="internalExternal">{(msg) => <Message error visible content={msg} />}</ErrorMessage>
            </Grid.Column>
          </Grid.Row>

          <InternalExternalComponent internalExternal={values.internalExternal} />

          <Grid.Row>
            <Grid.Column>
              <InputWithoutEnter
                name={'nominativoResponsabile'}
                label={t('Nominativo Responsabile Attività per ALSTOM')}
              />
            </Grid.Column>
          </Grid.Row>

          {values.attivitaAltoRischio.includes(IAttivitaAltoRischio.lavoriACaldo) && (
            <Grid.Row>
              <Grid.Column>
                <InputWithoutEnter
                  name={'nominativoSorveglianteAntincendio'}
                  label={t('Nominativo Sorvegliante Antincendio')}
                />
              </Grid.Column>
            </Grid.Row>
          )}

          <Grid.Row columns={1}>
            <Grid.Column>
              <Header size={'small'}>
                <TranslationComponent stringKey={"Ho ispezionato l'area prima dell'inizio dei lavori con esito"} />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column width={3}>
              <InputLabel>
                <Field type="radio" name="ispezioneArea" value="positivo" />
                <TranslationComponent stringKey={'Positivo'} />
              </InputLabel>
            </Grid.Column>
            <Grid.Column width={3}>
              <InputLabel>
                <Field type="radio" name="ispezioneArea" value="negativo" />
                <TranslationComponent stringKey={'Negativo'} />
              </InputLabel>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <ErrorMessage name="ispezioneArea">{(msg) => <Message error visible content={msg} />}</ErrorMessage>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </BorderedDiv>

      <BorderedDiv>
        <Header>
          <TranslationComponent stringKey={'Valutazione dei rischi'} />
        </Header>

        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column textAlign={'left'}>
              <Header size="small">
                <TranslationComponent stringKey={'DPI da utilizzare'} />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={5}>
            {Object.keys(DpiDescriptions).map((dpi) => (
              <Grid.Column key={dpi}>
                <InputLabel className={'label-dpi'}>
                  <Field type="checkbox" name="dpi" value={dpi} />
                  <span>
                    <TranslationComponent stringKey={dpi}></TranslationComponent>
                  </span>
                </InputLabel>
              </Grid.Column>
            ))}
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <DatePicker label={t('Scadenza guanti dielettrici')} name="scadenzaGuantiDielettrici" />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <TextArea name="dpiOther" label={t('Commenti')} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column textAlign={'left'}>
              <Header size="small">
                <TranslationComponent stringKey={'Mezzi estinguenti e sistemi di segnalazione'} />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={5}>
            {Object.keys(EstinguentiDescriptions).map((estinguenti) => (
              <Grid.Column key={estinguenti}>
                <InputLabel className={'label-estinguenti'}>
                  <Field type="checkbox" name="estinguenti" value={estinguenti} />
                  <span>
                    <TranslationComponent stringKey={estinguenti}></TranslationComponent>
                  </span>
                </InputLabel>
              </Grid.Column>
            ))}
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <TextArea name="estinguentiOther" label={t('Commenti')} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </BorderedDiv>

      {pdfExport && (
        <>
          <div style={{ breakAfter: 'page' }}></div>
          <div style={{ marginTop: pdfExport ? 20 : 0 }}>
            <Header textAlign={'center'}>{`EHS #${values.sequentialNumber} - ${values.name}`}</Header>
          </div>
        </>
      )}

      <MisureEControlliComponent
        showFirePrevention={values.attivitaAltoRischio.includes(IAttivitaAltoRischio.lavoriACaldo)}
        showConfinedSpaces={values.attivitaAltoRischio.includes(IAttivitaAltoRischio.spaziConfinati)}
      />

      <BorderedDiv style={{ marginTop: pdfExport ? 20 : 0 }}>
        <Header>
          <TranslationComponent stringKey={'Allegati'} />
        </Header>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <TranslationComponent stringKey={'Filename'} />
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {values.attachments?.map((file, index) => (
              <Table.Row key={index}>
                <Table.Cell>
                  {file._id ? (
                    <FileDownloadLink path={`/ehs/${values._id}/file/attachments/${file._id}`}>
                      {file.name}
                    </FileDownloadLink>
                  ) : (
                    file.name
                  )}
                </Table.Cell>
                <Table.Cell textAlign={'right'}>
                  <Button
                    icon
                    type={'button'}
                    onClick={() => {
                      setFieldValue(
                        'attachments',
                        values['attachments'].filter((_, i) => i !== index)
                      );
                    }}
                  >
                    <Icon name="remove circle" />
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
            <Table.Row>
              <Table.Cell colSpan={2}>
                <input
                  type="file"
                  onChange={(e) => {
                    setFieldValue('attachments', [...values['attachments'], e.target.files[0]]);
                    e.target.value = '';
                  }}
                />
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>

        <Header>
          <TranslationComponent stringKey={'Nominativi membri squadra/ditta esecutrice'} />
        </Header>

        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <TranslationComponent stringKey={'Nominativo'} />
              </Table.HeaderCell>
              <Table.HeaderCell>
                <TranslationComponent stringKey={'Firma'} />
              </Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>

          <Table.Body>
            <FieldArray name="execMembers">
              {({ remove, push }) => (
                <>
                  {values.execMembers?.map((member, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>{member.nominativo}</Table.Cell>
                      <Table.Cell>
                        <Image style={{ height: '40px' }} src={member.firma} />
                      </Table.Cell>
                      <Table.Cell textAlign={'right'}>
                        <SignatureModal
                          onSignature={(signature) => setFieldValue(`execMembers.${index}.firma`, signature)}
                        />
                        <Button icon type={'button'} onClick={() => remove(index)}>
                          <Icon name="remove circle" />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <ExecMemberAdd onAdd={(nominativo) => push({ nominativo })} />
                    </Table.Cell>
                  </Table.Row>
                </>
              )}
            </FieldArray>
          </Table.Body>
        </Table>

        <Header>
          <TranslationComponent
            stringKey={
              'Evidenze della formazione specifica /abilitazioni/qualifiche per le specifiche attività da svolgere per ALSTOM'
            }
          />
        </Header>

        <TextArea name="evidenzeFormazione" />
      </BorderedDiv>

      {pdfExport && (
        <>
          <div style={{ breakAfter: 'page' }}></div>
          <div style={{ marginTop: pdfExport ? 20 : 0 }}>
            <Header textAlign={'center'}>{`EHS #${values.sequentialNumber} - ${values.name}`}</Header>
          </div>
        </>
      )}

      <BorderedDiv>
        <Header>
          <TranslationComponent stringKey={'Data compilazione documento e approvazione attività'} />
        </Header>

        <Grid>
          <Grid.Row columns={6}>
            <Grid.Column>
              <DatePicker label={t('Date')} name="approvazioneDate" />
            </Grid.Column>
            <Grid.Column>
              <TimePickerComponent label={t('Hour')} name="approvazioneTime" inputProps={{ showSecond: false }} />
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <Header size={'small'}>
                <TranslationComponent stringKey={'Supervisore Alstom del lavoro da eseguire'} />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column>
              <InputWithoutEnter name={'supervisoreName'} label={t('Cognome e nome')} />
            </Grid.Column>
            <Grid.Column>
              <SignContainer>
                {touched.supervisoreSign && errors.supervisoreSign ? (
                  <Message error visible content={errors.supervisoreSign} />
                ) : (
                  <Image src={values.supervisoreSign} />
                )}
                <SignatureModal onSignature={(signature) => setFieldValue('supervisoreSign', signature)} />
              </SignContainer>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <Header size={'small'}>
                <TranslationComponent stringKey={'Chi consegna (Referente dell’area di lavoro selezionata)'} />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column>
              <InputWithoutEnter name={'referenteAreaName'} label={t('Cognome e nome')} />
            </Grid.Column>
            <Grid.Column>
              <SignContainer>
                {touched.referenteAreaSign && errors.referenteAreaSign ? (
                  <Message error visible content={errors.referenteAreaSign} />
                ) : (
                  <Image src={values.referenteAreaSign} />
                )}
                <SignatureModal onSignature={(signature) => setFieldValue('referenteAreaSign', signature)} />
              </SignContainer>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={1}>
            <Grid.Column>
              <Header size={'small'}>
                <TranslationComponent stringKey={'Chi prende in consegna (Referente attività ad alto rischio)'} />
              </Header>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row columns={2}>
            <Grid.Column>
              <InputWithoutEnter name={'referenteAttivitaName'} label={t('Cognome e nome')} />
            </Grid.Column>
            <Grid.Column>
              <SignContainer>
                {touched.referenteAttivitaSign && errors.referenteAttivitaSign ? (
                  <Message error visible content={errors.referenteAttivitaSign} />
                ) : (
                  <Image src={values.referenteAttivitaSign} />
                )}
                <SignatureModal onSignature={(signature) => setFieldValue('referenteAttivitaSign', signature)} />
              </SignContainer>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </BorderedDiv>

      <ResponsabiliIspezioniComponent />
    </>
  );
};
